import { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { dashboardApi, MoneyTransactionsResponse } from '../services/dashboardApi';

const NETWORK_LABELS: Record<string, string> = {
    'adtraction': 'Adtraction',
    'salus': 'Salus',
    'cake': 'SmartResponse',
    'has-offers': 'HasOffers',
    'linktrust': 'Orion',
    'addrevenue': 'AddRevenue',
    'tfbank': 'TF Bank',
    'traffego': 'Traffego',
};

const HASOFFERS_ACCOUNTS: Record<string, string> = {
    '16728': 'Axo',
    '13223': 'Nordic Affiliates',
    '28131': 'xlWin',
};

const networkLabel = (network: string, account: string | null) =>
    network === 'has-offers' && account && HASOFFERS_ACCOUNTS[account]
        ? HASOFFERS_ACCOUNTS[account]
        : NETWORK_LABELS[network] || network;

const TIER_LABELS: Record<string, string> = {
    low: 'Lav', mid: 'Mellem', high: 'Høj', payable: 'Payable',
    score_A: 'Score A', score_B: 'Score B', score_C: 'Score C', score_D: 'Score D', score_E: 'Score E', score_F: 'Score F',
};

const tierClass = (tier: string | null) => {
    if (!tier) return 'bg-gray-100 text-gray-500';
    if (['high', 'T1', 'score_A', 'score_B'].includes(tier)) return 'bg-green-100 text-green-800';
    if (['mid', 'T2', 'T3', 'payable', 'score_C', 'score_D'].includes(tier)) return 'bg-lime-100 text-lime-800';
    return 'bg-orange-100 text-orange-800';
};

const fmt = (n: number | null | undefined, digits = 0) =>
    n === null || n === undefined ? '–' : Number(n).toLocaleString('da-DK', { minimumFractionDigits: digits, maximumFractionDigits: digits });

const hoursBetween = (a: string | null, b: string) => {
    if (!a) return null;
    const h = (new Date(b).getTime() - new Date(a).getTime()) / 3_600_000;
    return h >= 0 ? h : null;
};

const PER_PAGE = 50;

interface Props {
    days: number;
    geo: string;
    esp: string;
}

/**
 * Transactions = money only (admin.affiliate_transactions_v): revenue > 0 and not declined.
 * 0-kr application events (Axo Lead Received, Sortter applications, ...) live under Applications.
 */
export default function MoneyTransactionsPanel({ days, geo, esp }: Props) {
    const [tier, setTier] = useState('all');
    const [network, setNetwork] = useState('all');
    const [data, setData] = useState<MoneyTransactionsResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [page, setPage] = useState(1);

    useEffect(() => {
        setLoading(true);
        setError('');
        setPage(1);
        dashboardApi.getMoneyTransactions(days, geo, esp, tier, network, 1000)
            .then(setData)
            .catch((e: any) => setError(e.message || 'Kunne ikke hente transaktioner'))
            .finally(() => setLoading(false));
    }, [days, geo, esp, tier, network]);

    const rows = data?.rows || [];
    const pages = Math.max(1, Math.ceil(rows.length / PER_PAGE));
    const pageRows = useMemo(() => rows.slice((page - 1) * PER_PAGE, page * PER_PAGE), [rows, page]);
    const k = data?.kpis;

    return (
        <div className="space-y-4">
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div>
                    <h3 className="text-lg font-semibold text-gray-800">Transaktioner (kun penge)</h3>
                    <p className="text-xs text-gray-500 max-w-3xl">
                        Kun konverteringer med omsætning &gt; 0 som ikke er afvist. 0-kr-hændelser (Axo "Lead Received", Sortter-ansøgninger m.m.) ligger under Ansøgninger.
                        {' '}Tier: Salus-score, Axo T1–T5/Payable, og beløbsbånd for Enklare/Enkelfinans/Ekonomen/Loans.
                    </p>
                </div>
                <div className="flex gap-2">
                    <select className="bg-gray-50 border border-gray-200 text-gray-700 text-sm rounded-lg p-2"
                        value={network} onChange={(e) => setNetwork(e.target.value)}>
                        <option value="all">Alle netværk</option>
                        {Object.entries(NETWORK_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                    <select className="bg-gray-50 border border-gray-200 text-gray-700 text-sm rounded-lg p-2"
                        value={tier} onChange={(e) => setTier(e.target.value)}>
                        <option value="all">Alle tiers</option>
                        <option value="high">Høj</option>
                        <option value="mid">Mellem</option>
                        <option value="low">Lav</option>
                        <option value="payable">Axo Payable</option>
                        {['T1', 'T2', 'T3', 'T4', 'T5'].map((t) => <option key={t} value={t}>Axo {t}</option>)}
                        {['A', 'B', 'C', 'D', 'E', 'F'].map((s) => <option key={s} value={`score_${s}`}>Salus {s}</option>)}
                        <option value="none">Uden tier</option>
                    </select>
                </div>
            </div>

            {error && <div className="bg-red-50 text-red-700 p-3 rounded-xl border border-red-200 text-sm">{error}</div>}

            <div className="rounded-sm shadow-sm border border-gray-200 bg-gray-200 gap-[1px] grid grid-cols-2 md:grid-cols-4 overflow-hidden">
                {[
                    ['Transaktioner', fmt(k?.transactions)],
                    ['Omsætning (DKK)', fmt(k?.revenue_dkk)],
                    ['Heraf pending (DKK)', k ? `${fmt(k.pending_dkk)} (${fmt(k.pending)})` : '–'],
                    ['Gns. pr. transaktion', k?.transactions ? fmt((k.revenue_dkk || 0) / k.transactions) : '–'],
                ].map(([title, value]) => (
                    <div key={title} className="bg-white p-4 flex flex-col items-center">
                        <div className="text-xs font-semibold text-gray-500 mb-1">{title}</div>
                        <div className="text-2xl text-gray-800 font-display">{loading ? '…' : value}</div>
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Pr. tier</h4>
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-xs text-gray-500 border-b">
                                <th className="py-1">Netværk</th><th className="py-1">Tier</th>
                                <th className="py-1 text-right">Antal</th><th className="py-1 text-right">DKK</th><th className="py-1 text-right">Gns.</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(data?.by_tier || []).map((r) => (
                                <tr key={`${r.network_id}-${r.tier}`} className="border-b last:border-0">
                                    <td className="py-1.5 text-gray-600">{NETWORK_LABELS[r.network_id] || r.network_id}</td>
                                    <td className="py-1.5"><span className={`px-2 py-0.5 rounded text-xs font-medium ${tierClass(r.tier)}`}>{TIER_LABELS[r.tier] || r.tier}</span></td>
                                    <td className="py-1.5 text-right">{fmt(r.transactions)}</td>
                                    <td className="py-1.5 text-right">{fmt(r.revenue_dkk)}</td>
                                    <td className="py-1.5 text-right font-medium">{fmt(r.avg_dkk)}</td>
                                </tr>
                            ))}
                            {!loading && (data?.by_tier || []).length === 0 && (
                                <tr><td colSpan={5} className="py-3 text-center text-gray-400 text-xs">Ingen transaktioner med tier</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 max-h-[420px] overflow-y-auto">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Pr. partner</h4>
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-xs text-gray-500 border-b">
                                <th className="py-1">Partner</th>
                                <th className="py-1 text-right">Antal</th><th className="py-1 text-right">DKK</th><th className="py-1 text-right">Gns.</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(data?.by_partner || []).map((r) => (
                                <tr key={`${r.network_id}-${r.advertiser_name}`} className="border-b last:border-0">
                                    <td className="py-1.5 max-w-[280px] truncate" title={r.advertiser_name}>
                                        <span className="text-[10px] text-gray-400 mr-1">{NETWORK_LABELS[r.network_id] || r.network_id}</span>{r.advertiser_name}
                                    </td>
                                    <td className="py-1.5 text-right">{fmt(r.transactions)}</td>
                                    <td className="py-1.5 text-right">{fmt(r.revenue_dkk)}</td>
                                    <td className="py-1.5 text-right text-gray-500">{fmt(r.avg_dkk)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="border-b border-gray-100 px-4 py-3 flex justify-between items-center">
                    <h4 className="text-sm font-semibold text-gray-700">Transaktioner {rows.length >= 1000 ? '(seneste 1.000)' : `(${fmt(rows.length)})`}</h4>
                    <div className="flex gap-2 text-sm items-center">
                        <span className="text-gray-500">Side {page} af {pages}</span>
                        <button disabled={page === 1} onClick={() => setPage((p) => p - 1)} className="p-1 rounded bg-gray-100 disabled:opacity-50"><ChevronLeft size={16} /></button>
                        <button disabled={page === pages} onClick={() => setPage((p) => p + 1)} className="p-1 rounded bg-gray-100 disabled:opacity-50"><ChevronRight size={16} /></button>
                    </div>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[1000px] text-sm whitespace-nowrap">
                        <thead>
                            <tr className="bg-gray-50 text-left text-xs text-gray-500 border-b">
                                <th className="px-4 py-2">Tid</th>
                                <th className="px-3 py-2">Netværk</th>
                                <th className="px-3 py-2">Partner</th>
                                <th className="px-3 py-2">sub1</th>
                                <th className="px-3 py-2">sub2 (lead)</th>
                                <th className="px-3 py-2">sub3</th>
                                <th className="px-3 py-2">Tier</th>
                                <th className="px-3 py-2">Status</th>
                                <th className="px-3 py-2 text-right">Ansøgning → betalt</th>
                                <th className="px-3 py-2 text-right">DKK</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {loading ? (
                                <tr><td colSpan={10} className="px-4 py-6 text-center text-gray-400">Henter…</td></tr>
                            ) : pageRows.map((r) => {
                                const h = hoursBetween(r.application_applied_at, r.timestamp);
                                return (
                                    <tr key={`${r.network_id}-${r.transaction_id}`} className="hover:bg-gray-50">
                                        <td className="px-4 py-2 text-gray-500">{new Date(r.timestamp).toLocaleString('da-DK')}</td>
                                        <td className="px-3 py-2 text-gray-600">{networkLabel(r.network_id, r.account)}</td>
                                        <td className="px-3 py-2 max-w-[260px] truncate" title={r.advertiser_name}>{r.advertiser_name}</td>
                                        <td className="px-3 py-2 text-xs text-gray-500">{r.sub1 || '–'}</td>
                                        <td className="px-3 py-2 text-xs font-mono text-gray-500">{r.sub2 || '–'}</td>
                                        <td className="px-3 py-2 text-xs text-gray-500">{r.sub3 || '–'}</td>
                                        <td className="px-3 py-2">
                                            {r.tier ? <span className={`px-2 py-0.5 rounded text-xs font-medium ${tierClass(r.tier)}`}>{TIER_LABELS[r.tier] || r.tier}</span> : <span className="text-gray-300">–</span>}
                                        </td>
                                        <td className="px-3 py-2">
                                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${r.status === 'approved' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>{r.status}</span>
                                        </td>
                                        <td className="px-3 py-2 text-right text-xs text-gray-500">
                                            {h === null ? '–' : h < 48 ? `${fmt(h, 1)} t` : `${fmt(h / 24, 1)} d`}
                                        </td>
                                        <td className="px-3 py-2 text-right font-semibold">{fmt(r.revenue_dkk, 2)}</td>
                                    </tr>
                                );
                            })}
                            {!loading && rows.length === 0 && (
                                <tr><td colSpan={10} className="px-4 py-6 text-center text-gray-400">Ingen transaktioner i filteret</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
